import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Loading } from '../Loading'
import { MarketSelector, marketSelectors } from '../../Config/Season/season'
import { getCurrentMarket, getMarketByName } from '../../Utils'
import { FastestShotLeaderboardItem } from '../../Api/fastestShot/fastestShot.types'
import { fetchFastestShotLeaderboards } from '../../Api'
import { Leaderboard } from './components/Leaderboard'
import {
  PageWrapper,
  HeaderContainer,
  PageTitle,
  LeaderboardsContainer,
  LoadingContainer,
  HeaderLogoCon,
} from './styles'
import { Img } from '../../Components/Image/Img'
import { PLL_IMAGES } from '../../Config/config'

export type FastestShotData = {
  markets: MarketSelector[]
  selectedMarket: MarketSelector | null
  overallLeaderboard: FastestShotLeaderboardItem[]
  locationLeaderboard: FastestShotLeaderboardItem[]
}
export type FastestShotLoading = {
  overallLeaderboard: boolean
  locationLeaderboard: boolean
}

const FastestShot: React.FC = () => {
  const [loading, setLoading] = useState<FastestShotLoading>({
    overallLeaderboard: true,
    locationLeaderboard: true,
  })
  const [data, setData] = useState<FastestShotData>({
    markets: [],
    selectedMarket: null,
    overallLeaderboard: [],
    locationLeaderboard: [],
  })

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const selectedMarketRef = useRef<MarketSelector | null>(null)

  useEffect(() => {
    selectedMarketRef.current = data.selectedMarket
  }, [data.selectedMarket])

  const getLeaderboards = useCallback(async (mkt: MarketSelector) => {
    const boards = await fetchFastestShotLeaderboards(mkt.name)
    if (boards?.overallLeaderboard && boards.overallLeaderboard.length < 1) return
    setData((prevData) => ({
      ...prevData,
      selectedMarket: mkt,
      overallLeaderboard: boards?.overallLeaderboard ?? [],
      locationLeaderboard: boards?.locationLeaderboard ?? [],
    }))
    return boards
  }, [])

  useEffect(() => {
    performInitialLoadAndSetupInterval()

    return () => {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
    }
  }, [getLeaderboards])

  const performInitialLoadAndSetupInterval = async () => {
    setLoading({ overallLeaderboard: true, locationLeaderboard: true })
    const urlParams = new URLSearchParams(window.location.search)
    const locationParam = urlParams.get('location')
    const initialMarket = locationParam
      ? getMarketByName(locationParam)
      : getCurrentMarket()

    setData((prevData) => ({
      ...prevData,
      markets: marketSelectors,
      selectedMarket: initialMarket,
    }))

    if (initialMarket) {
      await getLeaderboards(initialMarket)
    } else {
      console.warn('Initial market could not be determined.')
    }

    setLoading({ overallLeaderboard: false, locationLeaderboard: false })

    if (intervalIdRef.current === null && initialMarket) {
      intervalIdRef.current = setInterval(async () => {
        if (selectedMarketRef.current) {
          await getLeaderboards(selectedMarketRef.current)
        }
      }, 30000)
    }
  }

  const handleLocationChange = useCallback(
    async (marketName: string) => {
      const mkt = getMarketByName(marketName)
      if (mkt) {
        setLoading({ overallLeaderboard: false, locationLeaderboard: true })
        await getLeaderboards(mkt)
        setLoading({ overallLeaderboard: false, locationLeaderboard: false })
      } else {
        console.warn(`Market not found: ${marketName}`)
      }
    },
    [getLeaderboards]
  )

  if (loading.overallLeaderboard && loading.locationLeaderboard) {
    return (
      <PageWrapper>
        <HeaderContainer>
          <HeaderLogoCon>
            <Img src={PLL_IMAGES.ticketmasterLogo} alt="Ticketmaster Logo" />
          </HeaderLogoCon>
          <PageTitle>PLL Fastest Shot Tracker</PageTitle>
        </HeaderContainer>
        <LoadingContainer>
          <Loading />
        </LoadingContainer>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <HeaderContainer>
        <Img src={PLL_IMAGES.fastestShotBanner} alt="Fastest Shot Banner" />
      </HeaderContainer>
      <LeaderboardsContainer>
        <Leaderboard
          title="All Time"
          boardType="overall"
          leaderboard={data.overallLeaderboard}
        />
        <Leaderboard
          title={data.selectedMarket?.name ?? 'Weekend'}
          boardType="location"
          leaderboard={data.locationLeaderboard}
          handleLocationChange={handleLocationChange}
          availableMarkets={data.markets}
          selectedMarketName={data.selectedMarket?.name}
        />
      </LeaderboardsContainer>
    </PageWrapper>
  )
}

export default FastestShot
